import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { useEffect, useRef, useState } from 'react';
import { getPresenceList, PresenceParticipant } from '../endpoints/presence/list_GET.schema';
import {
  updatePresence,
  InputType as UpdatePresenceInput,
} from '../endpoints/presence/update_POST.schema';

// Poll less aggressively to avoid noisy network churn in long sessions.
const PRESENCE_POLL_INTERVAL = 12000;
const PRESENCE_UNFOCUSED_POLL_INTERVAL = 30000;
const PRESENCE_HIDDEN_POLL_INTERVAL = 45000;
// Send heartbeat every 5 seconds.
const HEARTBEAT_INTERVAL = 5000;

// Global polling manager to prevent duplicate polling
class PresencePollingManager {
  private static instance: PresencePollingManager;
  private pollingSessions = new Map<
    string,
    {
      refCount: number;
      timeoutId: number | null;
      lastData: PresenceParticipant[] | null;
      callbacks: Set<(data: PresenceParticipant[]) => void>;
      consecutiveErrors: number;
      inFlight: boolean;
      pollDelayMs: number;
      started: boolean;
      disposed: boolean;
    }
  >();

  static getInstance(): PresencePollingManager {
    const key = '__presencePollingManager';
    const globalAny = globalThis as any;
    if (!globalAny[key]) {
      globalAny[key] = new PresencePollingManager();
    }
    return globalAny[key] as PresencePollingManager;
  }

  subscribe(sessionId: string, callback: (data: PresenceParticipant[]) => void): () => void {
    let session = this.pollingSessions.get(sessionId);
    if (!session) {
      session = {
        refCount: 0,
        timeoutId: null,
        lastData: null,
        callbacks: new Set(),
        consecutiveErrors: 0,
        inFlight: false,
        pollDelayMs: PRESENCE_POLL_INTERVAL,
        started: false,
        disposed: false,
      };
      this.pollingSessions.set(sessionId, session);
    }

    session.callbacks.add(callback);
    session.refCount++;

    // Start polling if this is the first subscriber
    if (session.refCount === 1) {
      this.startPolling(sessionId, session);
    } else if (session.lastData) {
      // Send current data to new subscriber immediately
      callback(session.lastData);
    }

    // Return unsubscribe function
    return () => {
      session!.callbacks.delete(callback);
      session!.refCount--;

      if (session!.refCount === 0) {
        this.stopPolling(sessionId, session!);
        this.pollingSessions.delete(sessionId);
      }
    };
  }

  private async startPolling(sessionId: string, session: any) {
    if (session.started) return;
    session.started = true;
    session.disposed = false;

    const poll = async () => {
      if (session.disposed || session.refCount <= 0) return;
      if (session.inFlight) return;
      if (
        typeof document !== 'undefined' &&
        (document.visibilityState === 'hidden' || !document.hasFocus())
      ) {
        // Back off hard when hidden/unfocused.
        session.pollDelayMs =
          document.visibilityState === 'hidden'
            ? PRESENCE_HIDDEN_POLL_INTERVAL
            : PRESENCE_UNFOCUSED_POLL_INTERVAL;
        return;
      }
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        session.pollDelayMs = PRESENCE_HIDDEN_POLL_INTERVAL;
        return;
      }
      session.inFlight = true;
      try {
        const response = await getPresenceList({ sessionId });
        const participants = response.participants ?? [];
        session.consecutiveErrors = 0; // Reset on success
        session.pollDelayMs = PRESENCE_POLL_INTERVAL;

        // Simple comparison to avoid notifying subscribers when nothing meaningful changed.
        // We ignore volatile fields like lastSeenAt for stability (AI uses current timestamp each poll).
        const participantsChanged = (() => {
          const prev = session.lastData;
          if (!prev) return true;
          if (prev.length !== participants.length) return true;
          for (let i = 0; i < participants.length; i++) {
            const a = prev[i];
            const b = participants[i];
            if (!a || !b) return true;
            if (a.userId !== b.userId) return true;
            if ((a as any).isAi !== (b as any).isAi) return true;
            if (a.displayName !== b.displayName) return true;
            if (a.status !== b.status) return true;
            // currentFile may change often - include it to show activity
            if (a.currentFile !== b.currentFile) return true;
          }
          return false;
        })();

        if (!participantsChanged) {
          // No meaningful change - skip notifying and gradually back off polling.
          session.pollDelayMs = Math.min(session.pollDelayMs + 5000, PRESENCE_UNFOCUSED_POLL_INTERVAL);
          return;
        }

        session.lastData = participants;

        // Notify all subscribers
        session.callbacks.forEach((callback: (data: PresenceParticipant[]) => void) => {
          callback(participants);
        });
      } catch (error) {
        session.consecutiveErrors = (session.consecutiveErrors || 0) + 1;
        // Only log the first few errors to avoid console flood
        if (session.consecutiveErrors <= 3) {
          console.warn('[presence] polling failed:', error);
        }
        // After 5+ consecutive errors, switch to slow polling to avoid hammering server
        if (session.consecutiveErrors >= 5) session.pollDelayMs = 30000;
      } finally {
        session.inFlight = false;
      }
    };

    const tick = async () => {
      if (session.disposed || session.refCount <= 0) return;
      await poll();
      if (session.disposed || session.refCount <= 0) return;
      // Add slight jitter so many clients don't refetch in lockstep.
      const jitter = Math.floor(Math.random() * 1200);
      session.timeoutId = window.setTimeout(tick, session.pollDelayMs + jitter);
    };

    void tick();
  }

  private stopPolling(sessionId: string, session: any) {
    session.disposed = true;
    session.started = false;
    if (session.timeoutId) {
      clearTimeout(session.timeoutId);
      session.timeoutId = null;
    }
  }
}

export const usePresence = (sessionId: string | undefined) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['presence', sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return getPresenceList({ sessionId });
    },
    enabled: !!sessionId && false, // Disable automatic polling
    // Don't refetch on window focus as we handle polling manually
    refetchOnWindowFocus: false,
    // Keep data fresh for 30 seconds
    staleTime: 30000,
  });
};

// Alternative hook that uses centralized polling
export const usePresenceCentralized = (sessionId: string | undefined) => {
  const [data, setData] = useState<PresenceParticipant[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!sessionId) return;

    setIsLoading(true);
    const unsubscribe = PresencePollingManager.getInstance().subscribe(sessionId, (newData) => {
      setData(newData);
      setIsLoading(false);
      setError(null);
    });

    return unsubscribe;
  }, [sessionId]);

  return { data, isLoading, error };
};

export const useUpdatePresence = () => {
  // We don't invalidate queries here because we are polling anyway,
  // and we don't want to trigger a refetch immediately after every cursor move which would be too frequent.
  return useMutation({
    mutationFn: updatePresence,
  });
};

type HeartbeatParams = {
  sessionId: string | undefined;
  currentFile: string | null;
  cursorLine: number | null;
  cursorColumn: number | null;
  selection?: {
    startLine: number | null;
    startColumn: number | null;
    endLine: number | null;
    endColumn: number | null;
  } | null;
};

export const usePresenceHeartbeat = ({
  sessionId,
  currentFile,
  cursorLine,
  cursorColumn,
  selection,
}: HeartbeatParams) => {
  // Centralized per-session manager to ensure a single heartbeat interval and
  // avoid overlapping requests across multiple mounted editors/components.

  type ManagerState = {
    refCount: number;
    latest: HeartbeatParams | null;
    lastSentJson?: string | null;
    intervalId?: number | null;
    backoffTimeoutId?: number | null;
    inFlight: boolean;
    consecutiveErrors: number;
    stop: () => void;
    start: () => void;
    touch: (data: HeartbeatParams) => void;
  };

  const getOrCreateManager = (sid: string): ManagerState => {
    const mapKey = '__presenceManagers';
    const globalMap =
      (globalThis as any)[mapKey] ??
      ((globalThis as any)[mapKey] = new Map<string, ManagerState>());
    if (globalMap.has(sid)) return globalMap.get(sid)!;

    const manager: ManagerState = {
      refCount: 0,
      latest: null,
      lastSentJson: null,
      intervalId: null,
      backoffTimeoutId: null,
      inFlight: false,
      consecutiveErrors: 0,
      stop() {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
        if (this.backoffTimeoutId) {
          clearTimeout(this.backoffTimeoutId);
          this.backoffTimeoutId = null;
        }
      },
      start() {
        if (this.intervalId) return;

        const send = async () => {
          if (this.inFlight) return;
          if (!this.latest || !this.latest.sessionId) return;
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

          // Require at least one meaningful field to avoid 400s
          // Treat `undefined` the same as `null` here (some callers pass undefined).
          const hasPayload =
            (this.latest.currentFile != null && this.latest.currentFile !== '') ||
            this.latest.cursorLine != null ||
            this.latest.cursorColumn != null ||
            this.latest.selection != null;
          if (!hasPayload) return;

          const payload: UpdatePresenceInput = {
            sessionId: this.latest.sessionId!,
            currentFile: this.latest.currentFile ?? undefined,
            cursorLine: this.latest.cursorLine ?? undefined,
            cursorColumn: this.latest.cursorColumn ?? undefined,
            selection: this.latest.selection ?? undefined,
          };

          const payloadJson = JSON.stringify(payload);
          if (payloadJson === this.lastSentJson) return; // dedupe identical payloads

          this.inFlight = true;
          try {
            await updatePresence(payload as any);
            this.lastSentJson = payloadJson;
            this.consecutiveErrors = 0;
          } catch (err: any) {
            this.consecutiveErrors += 1;
            // If we keep failing, back off to avoid flooding the server
            if (this.consecutiveErrors >= 4 && !this.backoffTimeoutId) {
              // stop interval temporarily and schedule a retry later
              if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
              }
              const backoffMs = 30000; // 30s backoff

              this.backoffTimeoutId = window.setTimeout(() => {
                this.backoffTimeoutId = null;
                this.consecutiveErrors = 0;
                this.start();
              }, backoffMs);
            }
          } finally {
            this.inFlight = false;
          }
        };

        // Send immediately and then on interval
        void send();
        this.intervalId = window.setInterval(send, HEARTBEAT_INTERVAL);
      },
      touch(data: HeartbeatParams) {
        this.latest = data;
      },
    };

    globalMap.set(sid, manager);
    return manager;
  };

  useEffect(() => {
    if (!sessionId) return;

    const manager = getOrCreateManager(sessionId);
    manager.refCount += 1;

    // Update latest snapshot immediately
    manager.touch({ sessionId, currentFile, cursorLine, cursorColumn, selection });
    manager.start();

    return () => {
      manager.refCount -= 1;
      if (manager.refCount <= 0) {
        manager.stop();
        const mapKey = '__presenceManagers';
        const globalMap = (globalThis as any)[mapKey] ?? null;
        if (globalMap) globalMap.delete(sessionId);
      }
    };
    // We intentionally omit 'selection' from deps expansion as it is included below in the effect body update call
  }, [sessionId]);

  // Keep updating latest values without tearing down the manager
  useEffect(() => {
    if (!sessionId) return;
    const mapKey = '__presenceManagers';
    const globalMap = (globalThis as any)[mapKey] ?? null;
    const manager: ManagerState | undefined = globalMap?.get(sessionId);
    if (!manager) return;
    manager.touch({ sessionId, currentFile, cursorLine, cursorColumn, selection });
  }, [sessionId, currentFile, cursorLine, cursorColumn, selection]);
};
